import React from "react";
import { Card } from ".";
import { LucideIcon, CheckCircle, XCircle, FileDown, AlertTriangle, Info } from "lucide-react";

//#region 型定義
/**
 * 変換結果のプロパティ型
 */
interface ConversionResultProps {
  /** 変換結果データ */
  result: {
    /** 処理成功フラグ */
    success: boolean;
    /** メッセージ */
    message: string;
    /** 変換後のファイルパス */
    convertedFilePath: string | null;
    /** エラー詳細 */
    errorDetails?: string;
    /** 警告メッセージ */
    warnings?: string[];
    /** 追加情報 */
    additionalInfo?: { [key: string]: string };
  };
}
//#endregion

/**
 * 結果表示用のスタイル定義
 */
type ResultStyle = {
  containerClass: string;
  iconClass: string;
  textClass: string;
  borderClass: string;
  icon: LucideIcon;
};

/**
 * 変換結果表示コンポーネント
 * @description 図面変換の結果を表示するコンポーネント
 */
export const ConversionResult: React.FC<ConversionResultProps> = React.memo(({ result }) => {
  //#region スタイル定義
  /**
   * 結果の状態に応じたスタイルを取得する
   */
  const getStatusStyles = (): ResultStyle => {
    const styles: Record<'success' | 'error', ResultStyle> = {
      success: {
        containerClass: "bg-green-50",
        iconClass: "text-green-500",
        textClass: "text-green-800",
        borderClass: "border-green-200",
        icon: CheckCircle,
      },
      error: {
        containerClass: "bg-red-50",
        iconClass: "text-red-500",
        textClass: "text-red-800",
        borderClass: "border-red-200",
        icon: XCircle,
      },
    };

    return result.success ? styles.success : styles.error;
  };
  //#endregion

  //#region ファイル名処理
  /**
   * パスからファイル名を抽出する
   * @param path ファイルパス
   * @returns ファイル名
   */
  const extractFileName = (path: string): string => {
    return path.split(/[\/\\]/).pop() || path;
  };
  //#endregion

  //#region 追加情報の表示処理
  /**
   * 警告メッセージを表示する
   */
  const renderWarnings = () => {
    if (!result.warnings?.length) return null;

    return (
      <div className="mt-4 p-3 bg-yellow-50 rounded-md border border-yellow-200">
        <div className="flex items-center text-yellow-800 mb-2">
          <AlertTriangle className="mr-2 h-4 w-4" />
          <span className="font-medium">警告</span>
        </div>
        <ul className="list-disc list-inside space-y-1">
          {result.warnings.map((warning, index) => (
            <li key={index} className="text-sm text-yellow-700">
              {warning}
            </li>
          ))}
        </ul>
      </div>
    );
  };

  /**
   * 追加情報を表示する
   */
  const renderAdditionalInfo = () => {
    if (!result.additionalInfo) return null;

    return (
      <div className="mt-4 p-3 bg-blue-50 rounded-md border border-blue-200">
        <div className="flex items-center text-blue-800 mb-2">
          <Info className="mr-2 h-4 w-4" />
          <span className="font-medium">追加情報</span>
        </div>
        <dl className="grid grid-cols-2 gap-2 text-sm">
          {Object.entries(result.additionalInfo).map(([key, value]) => (
            <React.Fragment key={key}>
              <dt className="text-blue-700 font-medium">{key}:</dt>
              <dd className="text-blue-600">{value}</dd>
            </React.Fragment>
          ))}
        </dl>
      </div>
    );
  };
  //#endregion

  //#region UIレンダリング
  const styles = getStatusStyles();
  const StatusIcon = styles.icon;

  return (
    <Card
      shadow="sm"
      className={`border ${styles.borderClass} ${styles.containerClass}`}
    >
      <div className="p-4">
        {/* ステータスヘッダー */}
        <div className="flex items-start space-x-4">
          <div className={`flex-shrink-0 ${styles.iconClass}`}>
            <StatusIcon className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h3 className={`text-lg font-medium ${styles.textClass}`}>
              {result.success ? "変換成功" : "変換失敗"}
            </h3>
            <p className={`mt-1 text-sm ${styles.textClass}`}>
              {result.message}
            </p>
          </div>
        </div>

        {/* 変換ファイル情報 */}
        {result.success && result.convertedFilePath && (
          <div className="mt-4 text-sm">
            <div className={`p-3 rounded-md ${styles.containerClass} border ${styles.borderClass}`}>
              <div className="flex items-center space-x-2">
                <FileDown className={`h-4 w-4 ${styles.iconClass}`} />
                <span className="font-medium">変換後のファイル:</span>
                <span className="font-mono">
                  {extractFileName(result.convertedFilePath)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* エラー詳細 */}
        {!result.success && result.errorDetails && (
          <div className="mt-4 p-3 bg-red-50 rounded-md border border-red-200">
            <h4 className="text-sm font-medium text-red-800 mb-2">エラー詳細</h4>
            <p className="text-sm text-red-700">{result.errorDetails}</p>
          </div>
        )}

        {/* 警告メッセージ */}
        {renderWarnings()}

        {/* 追加情報 */}
        {renderAdditionalInfo()}

        {/* フッター情報 */}
        {result.success && (
          <div className="mt-4 text-xs text-gray-500">
            ※ 変換されたファイルは一定時間後に自動的に削除されます。
            必要なファイルは必ずダウンロードしてください。
          </div>
        )}
      </div>
    </Card>
  );
  //#endregion
});

// DisplayNameの設定
ConversionResult.displayName = "ConversionResult";

export default ConversionResult;